"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { initPostHog, whenReady } from "@/lib/posthog";
import { trackEvent } from "@/lib/posthog-helpers";

/**
 * Page views, and nothing that needs to know who the visitor is.
 *
 * Split out of the old posthog-provider on 2026-08-25. That component also identified the
 * signed-in member, which meant importing the auth store, which imports the Supabase
 * browser client: 230KB on every marketing page, to identify a visitor who by definition
 * has not signed in. The identify half now lives in components/app-runtime.tsx, which only
 * the app mounts. This half stays everywhere, so the website keeps its page views.
 *
 * Nothing here starts until consent is granted: initPostHog returns early without it.
 */
function Tracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    /* The URL is read now rather than inside the callback: PostHog is fetched on demand, and
       by the time it arrives the visitor may be on a different page. */
    const url = window.location.href;
    void whenReady().then((ph) => ph?.capture("$pageview", { $current_url: url }));
  }, [pathname, searchParams]);

  return null;
}

export function PostHogPageTracker() {
  useEffect(() => {
    void initPostHog();

    // Once per browser session, not on every navigation.
    try {
      if (!sessionStorage.getItem("ph_app_opened")) {
        sessionStorage.setItem("ph_app_opened", "1");
        trackEvent("app_opened", {
          pathname: window.location.pathname,
          referrer: document.referrer || undefined,
        });
      }
    } catch {
      // Ignore sessionStorage errors (private browsing, storage disabled).
    }
  }, []);

  return (
    // useSearchParams has to sit inside Suspense or the build fails.
    <Suspense fallback={null}>
      <Tracker />
    </Suspense>
  );
}
